import React, { useState, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import TaskDetailsModal from './TaskDetailsModal';
import AddTaskModal from './AddTaskModal';
import { useApiHandler } from '../../../utils/apiHandler';

function TaskModal({ project, onClose }) {
    const [selectedTask, setSelectedTask] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [showAddTaskModal, setShowAddTaskModal] = useState(false);
    const apiHandler = useApiHandler();

    useEffect(() => {
        const fetchTasks = async () => {
            try {
                const response = await apiHandler.get(`/admin/all-task?projectId=${project._id}`);
                if (response.data.success) {
                    setTasks(response.data.data);
                }
            } catch (error) {
                console.error('Error fetching tasks:', error);
                alert('There was an error fetching the tasks.');
            }
        };

        if (project._id) {
            fetchTasks();
        }
    }, [project._id]);

    const handleUpdateTaskStatus = (taskId, newStatus) => {
        const updatedTasks = tasks.map((task) =>
            task._id === taskId ? { ...task, status: newStatus } : task
        );
        setTasks(updatedTasks);
    };

    const handleAddComment = (taskId, comment) => {
        const updatedTasks = tasks.map((task) =>
            task._id === taskId
                ? {
                    ...task,
                    comments: [...(task.comments || []), comment],
                }
                : task
        );
        setTasks(updatedTasks);
    };

    const handleTaskAdded = (newTask) => {
        // Check if the new task has an employeeId with a name and assign it to employeeName
        if (newTask.employeeId && newTask.employeeId.name) {
            newTask.employeeName = newTask.employeeId.name;
        }
    
        // Add the new task to the tasks list
        setTasks((prevTasks) => [...prevTasks, newTask]);
        setShowAddTaskModal(false);
    };
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full p-6">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                            {project.projectName || 'Untitled Project'} - Tasks
                        </h2>
                        <p className="text-sm text-gray-600">Manage tasks for this project</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="mb-4">
                    <button
                        onClick={() => setShowAddTaskModal(true)}
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                    >
                        <Plus className="h-4 w-4" />
                        Add New Task
                    </button>
                </div>

                <div className="space-y-3 max-h-72 overflow-y-auto">
                    {tasks.map((task) => (
                        <div
                            key={task._id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                            onClick={() => setSelectedTask(task)}
                        >
                            <div>
                                <h4 className="font-medium text-gray-900">{task.taskDetails}</h4>
                                <p className="text-sm text-gray-600">{task.description}</p>
                                <p className="text-sm text-gray-600">
                                    Assigned to: {task.employeeName ? task.employeeName : 'Unknown Employee'}
                                    {task.comments?.length > 0 && (
                                        <span className="ml-2 text-blue-600">
                                            ({task.comments.length} comments)
                                        </span>
                                    )}
                                </p>
                            </div>
                            <span
                                className={`px-3 py-1 rounded-full text-sm ${task.status === 'Completed'
                                    ? 'bg-green-100 text-green-800'
                                    : task.status === 'In Progress'
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : 'bg-gray-100 text-gray-800'
                                    }`}
                            >
                                {task.status}
                            </span>
                        </div>
                    ))}
                </div>

                {selectedTask && (
                    <TaskDetailsModal
                        task={selectedTask}
                        onClose={() => setSelectedTask(null)}
                        onUpdateStatus={handleUpdateTaskStatus}
                        onAddComment={handleAddComment}
                    />
                )}

                {showAddTaskModal && (
                    <AddTaskModal
                        project={project}
                        onClose={() => setShowAddTaskModal(false)}
                        onTaskAdded={handleTaskAdded}
                    />
                )}
            </div>
        </div>
    );
}

export default TaskModal;
